/**
 * Municipios de Colombia por departamento, con el centroide de cada uno — dataset oficial
 * del DANE (DIVIPOLA).
 *
 * Fuente: DANE — "DIVIPOLA - Códigos municipios", publicado en datos.gov.co (dataset `gdxc-w37w`).
 * Endpoint: https://www.datos.gov.co/resource/gdxc-w37w.csv?$limit=5000
 *   (sin `$limit` el recurso devuelve sólo 1.000 filas).
 * Corte: 30 de diciembre de 2024.
 * Licencia: CC-BY-SA 4.0. Atribución obligatoria al DANE.
 *
 * Conteos verificados: 1.122 filas = 1.103 municipios + 18 áreas no municipalizadas + 1 isla,
 * distribuidos en los 33 departamentos. **Se incluyen las áreas no municipalizadas y la isla**:
 * son divisiones administrativas válidas y excluirlas bloquearía sitios legítimos.
 *
 * Las claves son exactamente las de `COLOMBIA_DEPARTMENTS`. Cada municipio guarda su nombre
 * (igual que antes: el formulario compara por nombre y el contrato persiste strings) y el
 * centroide `lat`/`lng` que trae el dataset. Las coordenadas del DANE vienen con coma decimal
 * (`-75,581775`); acá ya están normalizadas a punto. Dentro de cada departamento los municipios
 * van ordenados alfabéticamente.
 */
export interface MunicipalityCentroid {
  readonly name: string;
  readonly lat: number;
  readonly lng: number;
}

export const COLOMBIA_MUNICIPALITIES: Readonly<
  Record<string, readonly MunicipalityCentroid[]>
> = {
  Amazonas: [
    { name: 'El Encanto', lat: -1.74806, lng: -73.207114 },
    { name: 'La Chorrera', lat: -1.442617, lng: -72.791889 },
    { name: 'La Pedrera', lat: -1.320301, lng: -69.585499 },
    { name: 'La Victoria', lat: 0.054936, lng: -71.223208 },
    { name: 'Leticia', lat: -4.19895, lng: -69.941721 },
    { name: 'Mirití - Paraná', lat: -0.888833, lng: -70.98893 },
    { name: 'Puerto Alegría', lat: -1.005674, lng: -74.014461 },
    { name: 'Puerto Arica', lat: -2.147039, lng: -71.752186 },
    { name: 'Puerto Nariño', lat: -3.779934, lng: -70.364937 },
    { name: 'Puerto Santander', lat: -0.621184, lng: -72.384213 },
    { name: 'Tarapacá', lat: -2.890126, lng: -69.741745 },
  ],
  Antioquia: [
    { name: 'Abejorral', lat: 5.789315, lng: -75.428739 },
    { name: 'Abriaquí', lat: 6.632282, lng: -76.064304 },
    { name: 'Alejandría', lat: 6.376061, lng: -75.141346 },
    { name: 'Amagá', lat: 6.038708, lng: -75.702188 },
    { name: 'Amalfi', lat: 6.909655, lng: -75.077501 },
    { name: 'Andes', lat: 5.657194, lng: -75.878828 },
    { name: 'Angelópolis', lat: 6.109719, lng: -75.711389 },
    { name: 'Angostura', lat: 6.885175, lng: -75.335116 },
    { name: 'Anorí', lat: 7.074703, lng: -75.148355 },
    { name: 'Anzá', lat: 6.302641, lng: -75.854442 },
    { name: 'Apartadó', lat: 7.882968, lng: -76.625279 },
    { name: 'Arboletes', lat: 8.849317, lng: -76.426708 },
    { name: 'Argelia', lat: 5.731474, lng: -75.14107 },
    { name: 'Armenia', lat: 6.155667, lng: -75.786647 },
    { name: 'Barbosa', lat: 6.439195, lng: -75.331627 },
    { name: 'Bello', lat: 6.333587, lng: -75.555245 },
    { name: 'Belmira', lat: 6.606319, lng: -75.667779 },
    { name: 'Betania', lat: 5.74615, lng: -75.97679 },
    { name: 'Betulia', lat: 6.115208, lng: -75.984452 },
    { name: 'Briceño', lat: 7.112803, lng: -75.55036 },
    { name: 'Buriticá', lat: 6.720759, lng: -75.907 },
    { name: 'Cáceres', lat: 7.578366, lng: -75.35205 },
    { name: 'Caicedo', lat: 6.405607, lng: -75.98293 },
    { name: 'Caldas', lat: 6.091077, lng: -75.633673 },
    { name: 'Campamento', lat: 6.979771, lng: -75.298091 },
    { name: 'Cañasgordas', lat: 6.753859, lng: -76.028228 },
    { name: 'Caracolí', lat: 6.409829, lng: -74.757421 },
    { name: 'Caramanta', lat: 5.54853, lng: -75.643868 },
    { name: 'Carepa', lat: 7.755148, lng: -76.652652 },
    { name: 'Carolina', lat: 6.725995, lng: -75.283192 },
    { name: 'Caucasia', lat: 7.977278, lng: -75.197996 },
    { name: 'Chigorodó', lat: 7.666147, lng: -76.681531 },
    { name: 'Cisneros', lat: 6.537829, lng: -75.087047 },
    { name: 'Ciudad Bolívar', lat: 5.850273, lng: -76.021509 },
    { name: 'Cocorná', lat: 6.058295, lng: -75.185483 },
    { name: 'Concepción', lat: 6.394348, lng: -75.257587 },
    { name: 'Concordia', lat: 6.045738, lng: -75.908448 },
    { name: 'Copacabana', lat: 6.348557, lng: -75.509384 },
    { name: 'Dabeiba', lat: 6.998112, lng: -76.261614 },
    { name: 'Donmatías', lat: 6.485603, lng: -75.39263 },
    { name: 'Ebéjico', lat: 6.325615, lng: -75.766413 },
    { name: 'El Bagre', lat: 7.5975, lng: -74.799097 },
    { name: 'El Carmen de Viboral', lat: 6.082885, lng: -75.333901 },
    { name: 'El Santuario', lat: 6.136871, lng: -75.265465 },
    { name: 'Entrerríos', lat: 6.566273, lng: -75.517685 },
    { name: 'Envigado', lat: 6.166695, lng: -75.582192 },
    { name: 'Fredonia', lat: 5.928039, lng: -75.675072 },
    { name: 'Frontino', lat: 6.776066, lng: -76.130765 },
    { name: 'Giraldo', lat: 6.680808, lng: -75.952158 },
    { name: 'Girardota', lat: 6.379472, lng: -75.444238 },
    { name: 'Gómez Plata', lat: 6.683269, lng: -75.220018 },
    { name: 'Granada', lat: 6.142892, lng: -75.184446 },
    { name: 'Guadalupe', lat: 6.815069, lng: -75.239862 },
    { name: 'Guarne', lat: 6.27787, lng: -75.441612 },
    { name: 'Guatapé', lat: 6.232461, lng: -75.160041 },
    { name: 'Heliconia', lat: 6.206757, lng: -75.734322 },
    { name: 'Hispania', lat: 5.799461, lng: -75.906587 },
    { name: 'Itagüí', lat: 6.175079, lng: -75.612056 },
    { name: 'Ituango', lat: 7.171629, lng: -75.764673 },
    { name: 'Jardín', lat: 5.597542, lng: -75.818982 },
    { name: 'Jericó', lat: 5.789748, lng: -75.785499 },
    { name: 'La Ceja', lat: 6.028062, lng: -75.429433 },
    { name: 'La Estrella', lat: 6.145238, lng: -75.637708 },
    { name: 'La Pintada', lat: 5.743808, lng: -75.60781 },
    { name: 'La Unión', lat: 5.973845, lng: -75.360874 },
    { name: 'Liborina', lat: 6.677316, lng: -75.812838 },
    { name: 'Maceo', lat: 6.552116, lng: -74.78716 },
    { name: 'Marinilla', lat: 6.173995, lng: -75.339345 },
    { name: 'Medellín', lat: 6.246631, lng: -75.581775 },
    { name: 'Montebello', lat: 5.946313, lng: -75.523455 },
    { name: 'Murindó', lat: 6.97771, lng: -76.817485 },
    { name: 'Mutatá', lat: 7.242875, lng: -76.435875 },
    { name: 'Nariño', lat: 5.610777, lng: -75.176262 },
    { name: 'Nechí', lat: 8.094129, lng: -74.77647 },
    { name: 'Necoclí', lat: 8.434526, lng: -76.787271 },
    { name: 'Olaya', lat: 6.626492, lng: -75.811773 },
    { name: 'Peñol', lat: 6.219349, lng: -75.242693 },
    { name: 'Peque', lat: 7.021029, lng: -75.910357 },
    { name: 'Pueblorrico', lat: 5.79158, lng: -75.839903 },
    { name: 'Puerto Berrío', lat: 6.487028, lng: -74.410016 },
    { name: 'Puerto Nare', lat: 6.186025, lng: -74.583012 },
    { name: 'Puerto Triunfo', lat: 5.871318, lng: -74.64119 },
    { name: 'Remedios', lat: 7.029424, lng: -74.698135 },
    { name: 'Retiro', lat: 6.062454, lng: -75.501301 },
    { name: 'Rionegro', lat: 6.147148, lng: -75.377316 },
    { name: 'Sabanalarga', lat: 6.850028, lng: -75.816645 },
    { name: 'Sabaneta', lat: 6.149903, lng: -75.615479 },
    { name: 'Salgar', lat: 5.964198, lng: -75.976807 },
    { name: 'San Andrés de Cuerquía', lat: 6.916676, lng: -75.674564 },
    { name: 'San Carlos', lat: 6.187746, lng: -74.988097 },
    { name: 'San Francisco', lat: 5.963476, lng: -75.101562 },
    { name: 'San Jerónimo', lat: 6.44809, lng: -75.726975 },
    { name: 'San José de la Montaña', lat: 6.85009, lng: -75.683352 },
    { name: 'San Juan de Urabá', lat: 8.758964, lng: -76.52857 },
    { name: 'San Luis', lat: 6.043017, lng: -74.993619 },
    { name: 'San Pedro de los Milagros', lat: 6.46012, lng: -75.556743 },
    { name: 'San Pedro de Urabá', lat: 8.276884, lng: -76.380567 },
    { name: 'San Rafael', lat: 6.293759, lng: -75.02849 },
    { name: 'San Roque', lat: 6.485939, lng: -75.019109 },
    { name: 'San Vicente Ferrer', lat: 6.282164, lng: -75.332616 },
    { name: 'Santa Bárbara', lat: 5.875527, lng: -75.567351 },
    { name: 'Santa Fé de Antioquia', lat: 6.556484, lng: -75.826648 },
    { name: 'Santa Rosa de Osos', lat: 6.643366, lng: -75.460723 },
    { name: 'Santo Domingo', lat: 6.473032, lng: -75.164903 },
    { name: 'Segovia', lat: 7.079648, lng: -74.701596 },
    { name: 'Sonsón', lat: 5.714851, lng: -75.309596 },
    { name: 'Sopetrán', lat: 6.500745, lng: -75.747378 },
    { name: 'Támesis', lat: 5.664645, lng: -75.714429 },
    { name: 'Tarazá', lat: 7.580127, lng: -75.401407 },
    { name: 'Tarso', lat: 5.864542, lng: -75.822956 },
    { name: 'Titiribí', lat: 6.062391, lng: -75.791887 },
    { name: 'Toledo', lat: 7.010328, lng: -75.692281 },
    { name: 'Turbo', lat: 8.089929, lng: -76.728858 },
    { name: 'Uramita', lat: 6.898393, lng: -76.173284 },
    { name: 'Urrao', lat: 6.317343, lng: -76.133951 },
    { name: 'Valdivia', lat: 7.1652, lng: -75.439274 },
    { name: 'Valparaíso', lat: 5.614555, lng: -75.624452 },
    { name: 'Vegachí', lat: 6.773525, lng: -74.798714 },
    { name: 'Venecia', lat: 5.964693, lng: -75.735544 },
    { name: 'Vigía del Fuerte', lat: 6.588164, lng: -76.896004 },
    { name: 'Yalí', lat: 6.676554, lng: -74.840059 },
    { name: 'Yarumal', lat: 6.963832, lng: -75.418828 },
    { name: 'Yolombó', lat: 6.594511, lng: -75.013385 },
    { name: 'Yondó', lat: 7.00396, lng: -73.912445 },
    { name: 'Zaragoza', lat: 7.488583, lng: -74.867075 },
  ],
  Arauca: [
    { name: 'Arauca', lat: 7.072726, lng: -70.747408 },
    { name: 'Arauquita', lat: 7.02702, lng: -71.426733 },
    { name: 'Cravo Norte', lat: 6.303913, lng: -70.204286 },
    { name: 'Fortul', lat: 6.796695, lng: -71.76877 },
    { name: 'Puerto Rondón', lat: 6.281461, lng: -71.10339 },
    { name: 'Saravena', lat: 6.953926, lng: -71.872812 },
    { name: 'Tame', lat: 6.453324, lng: -71.754427 },
  ],
  'Atlántico': [
    { name: 'Baranoa', lat: 10.79445, lng: -74.916077 },
    { name: 'Barranquilla', lat: 10.977961, lng: -74.815546 },
    { name: 'Campo de la Cruz', lat: 10.378291, lng: -74.880847 },
    { name: 'Candelaria', lat: 10.461903, lng: -74.879717 },
    { name: 'Galapa', lat: 10.919033, lng: -74.870385 },
    { name: 'Juan de Acosta', lat: 10.83254, lng: -75.041032 },
    { name: 'Luruaco', lat: 10.610491, lng: -75.14199 },
    { name: 'Malambo', lat: 10.857086, lng: -74.776923 },
    { name: 'Manatí', lat: 10.449089, lng: -74.956867 },
    { name: 'Palmar de Varela', lat: 10.738591, lng: -74.754765 },
    { name: 'Piojó', lat: 10.749216, lng: -75.107592 },
    { name: 'Polonuevo', lat: 10.777363, lng: -74.852981 },
    { name: 'Ponedera', lat: 10.641779, lng: -74.753885 },
    { name: 'Puerto Colombia', lat: 11.015322, lng: -74.888627 },
    { name: 'Repelón', lat: 10.493357, lng: -75.125534 },
    { name: 'Sabanagrande', lat: 10.792453, lng: -74.759496 },
    { name: 'Sabanalarga', lat: 10.632091, lng: -74.921256 },
    { name: 'Santa Lucía', lat: 10.324303, lng: -74.959204 },
    { name: 'Santo Tomás', lat: 10.758735, lng: -74.757859 },
    { name: 'Soledad', lat: 10.909921, lng: -74.786054 },
    { name: 'Suan', lat: 10.335432, lng: -74.881687 },
    { name: 'Tubará', lat: 10.873586, lng: -74.978704 },
    { name: 'Usiacurí', lat: 10.74298, lng: -74.976985 },
  ],
  'Bolívar': [
    { name: 'Achí', lat: 8.570107, lng: -74.557676 },
    { name: 'Altos del Rosario', lat: 8.791865, lng: -74.164905 },
    { name: 'Arenal', lat: 8.458865, lng: -73.941099 },
    { name: 'Arjona', lat: 10.25666, lng: -75.344332 },
    { name: 'Arroyohondo', lat: 10.250075, lng: -75.019215 },
    { name: 'Barranco de Loba', lat: 8.947787, lng: -74.104391 },
    { name: 'Calamar', lat: 10.250431, lng: -74.916144 },
    { name: 'Cantagallo', lat: 7.378678, lng: -73.914605 },
    { name: 'Cartagena de Indias', lat: 10.385126, lng: -75.496269 },
    { name: 'Cicuco', lat: 9.274281, lng: -74.645981 },
    { name: 'Clemencia', lat: 10.567452, lng: -75.328469 },
    { name: 'Córdoba', lat: 9.586942, lng: -74.827399 },
    { name: 'El Carmen de Bolívar', lat: 9.718653, lng: -75.121178 },
    { name: 'El Guamo', lat: 10.030958, lng: -74.976084 },
    { name: 'El Peñón', lat: 8.988271, lng: -73.949274 },
    { name: 'Hatillo de Loba', lat: 8.956014, lng: -74.077912 },
    { name: 'Magangué', lat: 9.263799, lng: -74.766742 },
    { name: 'Mahates', lat: 10.233285, lng: -75.191643 },
    { name: 'Margarita', lat: 9.15784, lng: -74.285137 },
    { name: 'María la Baja', lat: 9.982402, lng: -75.300516 },
    { name: 'Montecristo', lat: 8.297234, lng: -74.471176 },
    { name: 'Morales', lat: 8.276558, lng: -73.868172 },
    { name: 'Norosí', lat: 8.526259, lng: -74.038003 },
    { name: 'Pinillos', lat: 8.914947, lng: -74.462279 },
    { name: 'Regidor', lat: 8.666258, lng: -73.821638 },
    { name: 'Río Viejo', lat: 8.58795, lng: -73.840466 },
    { name: 'San Cristóbal', lat: 10.392836, lng: -75.065076 },
    { name: 'San Estanislao', lat: 10.398602, lng: -75.153101 },
    { name: 'San Fernando', lat: 9.214183, lng: -74.323811 },
    { name: 'San Jacinto', lat: 9.830275, lng: -75.12105 },
    { name: 'San Jacinto del Cauca', lat: 8.25158, lng: -74.721156 },
    { name: 'San Juan Nepomuceno', lat: 9.953751, lng: -75.081761 },
    { name: 'San Martín de Loba', lat: 8.937485, lng: -74.039134 },
    { name: 'San Pablo', lat: 7.476747, lng: -73.924602 },
    { name: 'Santa Catalina', lat: 10.605294, lng: -75.287855 },
    { name: 'Santa Cruz de Mompox', lat: 9.244241, lng: -74.42818 },
    { name: 'Santa Rosa', lat: 10.444396, lng: -75.369824 },
    { name: 'Santa Rosa del Sur', lat: 7.963938, lng: -74.052243 },
    { name: 'Simití', lat: 7.953916, lng: -73.947264 },
    { name: 'Soplaviento', lat: 10.38839, lng: -75.136404 },
    { name: 'Talaigua Nuevo', lat: 9.30403, lng: -74.567479 },
    { name: 'Tiquisio', lat: 8.558666, lng: -74.262922 },
    { name: 'Turbaco', lat: 10.348316, lng: -75.427249 },
    { name: 'Turbaná', lat: 10.274585, lng: -75.44265 },
    { name: 'Villanueva', lat: 10.444089, lng: -75.275613 },
    { name: 'Zambrano', lat: 9.746306, lng: -74.817879 },
  ],
  'Boyacá': [
    { name: 'Almeida', lat: 4.970857, lng: -73.378933 },
    { name: 'Aquitania', lat: 5.518602, lng: -72.88399 },
    { name: 'Arcabuco', lat: 5.755673, lng: -73.437503 },
    { name: 'Belén', lat: 5.98923, lng: -72.911641 },
    { name: 'Berbeo', lat: 5.227451, lng: -73.12721 },
    { name: 'Betéitiva', lat: 5.909978, lng: -72.809014 },
    { name: 'Boavita', lat: 6.330703, lng: -72.584905 },
    { name: 'Boyacá', lat: 5.454578, lng: -73.361945 },
    { name: 'Briceño', lat: 5.690879, lng: -73.92326 },
    { name: 'Buenavista', lat: 5.512594, lng: -73.94217 },
    { name: 'Busbanzá', lat: 5.831393, lng: -72.884158 },
    { name: 'Caldas', lat: 5.55458, lng: -73.865553 },
    { name: 'Campohermoso', lat: 5.031676, lng: -73.104173 },
    { name: 'Cerinza', lat: 5.955939, lng: -72.947918 },
    { name: 'Chinavita', lat: 5.167486, lng: -73.368476 },
    { name: 'Chiquinquirá', lat: 5.61379, lng: -73.818745 },
    { name: 'Chíquiza', lat: 5.639834, lng: -73.449463 },
    { name: 'Chiscas', lat: 6.553136, lng: -72.500957 },
    { name: 'Chita', lat: 6.187083, lng: -72.471892 },
    { name: 'Chitaraque', lat: 6.027425, lng: -73.4471 },
    { name: 'Chivatá', lat: 5.558949, lng: -73.282529 },
    { name: 'Chivor', lat: 4.888173, lng: -73.368398 },
    { name: 'Ciénega', lat: 5.408694, lng: -73.296049 },
    { name: 'Cómbita', lat: 5.634545, lng: -73.323957 },
    { name: 'Coper', lat: 5.475074, lng: -74.045636 },
    { name: 'Corrales', lat: 5.828064, lng: -72.844795 },
    { name: 'Covarachía', lat: 6.500177, lng: -72.738978 },
    { name: 'Cubará', lat: 6.997275, lng: -72.107939 },
    { name: 'Cucaita', lat: 5.544452, lng: -73.454338 },
    { name: 'Cuítiva', lat: 5.580367, lng: -72.965923 },
    { name: 'Duitama', lat: 5.822964, lng: -73.03063 },
    { name: 'El Cocuy', lat: 6.407738, lng: -72.444537 },
    { name: 'El Espino', lat: 6.483027, lng: -72.497007 },
    { name: 'Firavitoba', lat: 5.668885, lng: -72.993392 },
    { name: 'Floresta', lat: 5.859519, lng: -72.918111 },
    { name: 'Gachantivá', lat: 5.751891, lng: -73.549092 },
    { name: 'Gámeza', lat: 5.802333, lng: -72.80553 },
    { name: 'Garagoa', lat: 5.083234, lng: -73.364413 },
    { name: 'Guacamayas', lat: 6.459667, lng: -72.500812 },
    { name: 'Guateque', lat: 5.007321, lng: -73.471207 },
    { name: 'Guayatá', lat: 4.967122, lng: -73.489698 },
    { name: 'Güicán de la Sierra', lat: 6.462864, lng: -72.411763 },
    { name: 'Iza', lat: 5.611577, lng: -72.979559 },
    { name: 'Jenesano', lat: 5.385813, lng: -73.363738 },
    { name: 'Jericó', lat: 6.145735, lng: -72.571122 },
    { name: 'La Capilla', lat: 5.095687, lng: -73.444347 },
    { name: 'La Uvita', lat: 6.31616, lng: -72.559982 },
    { name: 'La Victoria', lat: 5.523792, lng: -74.234393 },
    { name: 'Labranzagrande', lat: 5.562687, lng: -72.57777 },
    { name: 'Macanal', lat: 4.972464, lng: -73.319593 },
    { name: 'Maripí', lat: 5.550091, lng: -74.00405 },
    { name: 'Miraflores', lat: 5.196515, lng: -73.14563 },
    { name: 'Mongua', lat: 5.754242, lng: -72.79809 },
    { name: 'Monguí', lat: 5.723486, lng: -72.849292 },
    { name: 'Moniquirá', lat: 5.876331, lng: -73.573374 },
    { name: 'Motavita', lat: 5.5777, lng: -73.367841 },
    { name: 'Muzo', lat: 5.532758, lng: -74.10269 },
    { name: 'Nobsa', lat: 5.768046, lng: -72.937042 },
    { name: 'Nuevo Colón', lat: 5.355317, lng: -73.456759 },
    { name: 'Oicatá', lat: 5.595235, lng: -73.308399 },
    { name: 'Otanche', lat: 5.657536, lng: -74.180965 },
    { name: 'Pachavita', lat: 5.140065, lng: -73.396953 },
    { name: 'Páez', lat: 5.097319, lng: -73.052737 },
    { name: 'Paipa', lat: 5.779894, lng: -73.11782 },
    { name: 'Pajarito', lat: 5.293783, lng: -72.703231 },
    { name: 'Panqueba', lat: 6.443416, lng: -72.459424 },
    { name: 'Pauna', lat: 5.656323, lng: -73.978449 },
    { name: 'Paya', lat: 5.625699, lng: -72.423775 },
    { name: 'Paz de Río', lat: 5.987645, lng: -72.749137 },
    { name: 'Pesca', lat: 5.558808, lng: -73.050872 },
    { name: 'Pisba', lat: 5.72141, lng: -72.486023 },
    { name: 'Puerto Boyacá', lat: 5.976646, lng: -74.587782 },
    { name: 'Quípama', lat: 5.52055, lng: -74.180033 },
    { name: 'Ramiriquí', lat: 5.400303, lng: -73.334839 },
    { name: 'Ráquira', lat: 5.539136, lng: -73.632543 },
    { name: 'Rondón', lat: 5.357378, lng: -73.208474 },
    { name: 'Saboyá', lat: 5.697756, lng: -73.764456 },
    { name: 'Sáchica', lat: 5.584305, lng: -73.542539 },
    { name: 'Samacá', lat: 5.492161, lng: -73.485589 },
    { name: 'San Eduardo', lat: 5.22401, lng: -73.077747 },
    { name: 'San José de Pare', lat: 6.018924, lng: -73.545397 },
    { name: 'San Luis de Gaceno', lat: 4.81976, lng: -73.168076 },
    { name: 'San Mateo', lat: 6.401683, lng: -72.555264 },
    { name: 'San Miguel de Sema', lat: 5.518083, lng: -73.722009 },
    { name: 'San Pablo de Borbur', lat: 5.650743, lng: -74.069963 },
    { name: 'Santa María', lat: 4.857193, lng: -73.263518 },
    { name: 'Santa Rosa de Viterbo', lat: 5.874547, lng: -72.982461 },
    { name: 'Santa Sofía', lat: 5.713269, lng: -73.602707 },
    { name: 'Santana', lat: 6.056866, lng: -73.481639 },
    { name: 'Sativanorte', lat: 6.131132, lng: -72.708458 },
    { name: 'Sativasur', lat: 6.093183, lng: -72.712435 },
    { name: 'Siachoque', lat: 5.511811, lng: -73.24466 },
    { name: 'Soatá', lat: 6.331945, lng: -72.684051 },
    { name: 'Socha', lat: 5.996717, lng: -72.691963 },
    { name: 'Socotá', lat: 6.041162, lng: -72.636653 },
    { name: 'Sogamoso', lat: 5.723976, lng: -72.924355 },
    { name: 'Somondoco', lat: 4.985726, lng: -73.433393 },
    { name: 'Sora', lat: 5.56684, lng: -73.450153 },
    { name: 'Soracá', lat: 5.500898, lng: -73.332804 },
    { name: 'Sotaquirá', lat: 5.764986, lng: -73.246585 },
    { name: 'Susacón', lat: 6.230332, lng: -72.690289 },
    { name: 'Sutamarchán', lat: 5.619781, lng: -73.620536 },
    { name: 'Sutatenza', lat: 5.022989, lng: -73.452317 },
    { name: 'Tasco', lat: 5.909821, lng: -72.781011 },
    { name: 'Tenza', lat: 5.076781, lng: -73.421176 },
    { name: 'Tibaná', lat: 5.317251, lng: -73.396457 },
    { name: 'Tibasosa', lat: 5.74723, lng: -72.999449 },
    { name: 'Tinjacá', lat: 5.579713, lng: -73.646847 },
    { name: 'Tipacoque', lat: 6.419203, lng: -72.691729 },
    { name: 'Toca', lat: 5.566464, lng: -73.184794 },
    { name: 'Togüí', lat: 5.937438, lng: -73.513655 },
    { name: 'Tópaga', lat: 5.768201, lng: -72.832245 },
    { name: 'Tota', lat: 5.560497, lng: -72.985898 },
    { name: 'Tunja', lat: 5.53988, lng: -73.355539 },
    { name: 'Tununguá', lat: 5.730582, lng: -73.933155 },
    { name: 'Turmequé', lat: 5.323261, lng: -73.491825 },
    { name: 'Tuta', lat: 5.689082, lng: -73.230285 },
    { name: 'Tutazá', lat: 6.032608, lng: -72.856035 },
    { name: 'Úmbita', lat: 5.221176, lng: -73.456917 },
    { name: 'Ventaquemada', lat: 5.368739, lng: -73.522368 },
    { name: 'Villa de Leyva', lat: 5.632455, lng: -73.524948 },
    { name: 'Viracachá', lat: 5.436833, lng: -73.296894 },
    { name: 'Zetaquira', lat: 5.283443, lng: -73.17098 },
  ],
  Caldas: [
    { name: 'Aguadas', lat: 5.610244, lng: -75.45487 },
    { name: 'Anserma', lat: 5.236471, lng: -75.784343 },
    { name: 'Aranzazu', lat: 5.271195, lng: -75.49129 },
    { name: 'Belalcázar', lat: 4.993785, lng: -75.811918 },
    { name: 'Chinchiná', lat: 4.985227, lng: -75.607529 },
    { name: 'Filadelfia', lat: 5.297091, lng: -75.562474 },
    { name: 'La Dorada', lat: 5.460834, lng: -74.668819 },
    { name: 'La Merced', lat: 5.39647, lng: -75.546486 },
    { name: 'Manizales', lat: 5.057657, lng: -75.491025 },
    { name: 'Manzanares', lat: 5.255699, lng: -75.152829 },
    { name: 'Marmato', lat: 5.47422, lng: -75.600049 },
    { name: 'Marquetalia', lat: 5.297525, lng: -75.053097 },
    { name: 'Marulanda', lat: 5.284304, lng: -75.259721 },
    { name: 'Neira', lat: 5.166895, lng: -75.520006 },
    { name: 'Norcasia', lat: 5.574796, lng: -74.889543 },
    { name: 'Pácora', lat: 5.527172, lng: -75.459621 },
    { name: 'Palestina', lat: 5.017879, lng: -75.624577 },
    { name: 'Pensilvania', lat: 5.383281, lng: -75.160299 },
    { name: 'Riosucio', lat: 5.423673, lng: -75.702104 },
    { name: 'Risaralda', lat: 5.164509, lng: -75.76722 },
    { name: 'Salamina', lat: 5.403025, lng: -75.487223 },
    { name: 'Samaná', lat: 5.41308, lng: -74.992263 },
    { name: 'San José', lat: 5.08231, lng: -75.792063 },
    { name: 'Supía', lat: 5.446843, lng: -75.64966 },
    { name: 'Victoria', lat: 5.317437, lng: -74.911239 },
    { name: 'Villamaría', lat: 5.038925, lng: -75.502487 },
    { name: 'Viterbo', lat: 5.062664, lng: -75.87061 },
  ],
  'Caquetá': [
    { name: 'Albania', lat: 1.328526, lng: -75.878375 },
    { name: 'Belén de los Andaquíes', lat: 1.415812, lng: -75.872405 },
    { name: 'Cartagena del Chairá', lat: 1.332371, lng: -74.847867 },
    { name: 'Curillo', lat: 1.033473, lng: -75.919205 },
    { name: 'El Doncello', lat: 1.679951, lng: -75.283631 },
    { name: 'El Paujíl', lat: 1.570226, lng: -75.326093 },
    { name: 'Florencia', lat: 1.618196, lng: -75.609831 },
    { name: 'La Montañita', lat: 1.479173, lng: -75.436408 },
    { name: 'Milán', lat: 1.29021, lng: -75.506926 },
    { name: 'Morelia', lat: 1.486611, lng: -75.724146 },
    { name: 'Puerto Rico', lat: 1.909063, lng: -75.157604 },
    { name: 'San José del Fragua', lat: 1.330266, lng: -75.973796 },
    { name: 'San Vicente del Caguán', lat: 2.119413, lng: -74.767894 },
    { name: 'Solano', lat: 0.699077, lng: -75.253702 },
    { name: 'Solita', lat: 0.87654, lng: -75.619902 },
    { name: 'Valparaíso', lat: 1.194619, lng: -75.70671 },
  ],
  Casanare: [
    { name: 'Aguazul', lat: 5.172641, lng: -72.546838 },
    { name: 'Chámeza', lat: 5.214527, lng: -72.87016 },
    { name: 'Hato Corozal', lat: 6.154099, lng: -71.764213 },
    { name: 'La Salina', lat: 6.127762, lng: -72.334978 },
    { name: 'Maní', lat: 4.81681, lng: -72.281384 },
    { name: 'Monterrey', lat: 4.877017, lng: -72.894065 },
    { name: 'Nunchía', lat: 5.636474, lng: -72.195323 },
    { name: 'Orocué', lat: 4.790258, lng: -71.338533 },
    { name: 'Paz de Ariporo', lat: 5.879827, lng: -71.890348 },
    { name: 'Pore', lat: 5.72773, lng: -71.99286 },
    { name: 'Recetor', lat: 5.229181, lng: -72.760991 },
    { name: 'Sabanalarga', lat: 4.854787, lng: -73.038696 },
    { name: 'Sácama', lat: 6.096738, lng: -72.250157 },
    { name: 'San Luis de Palenque', lat: 5.422397, lng: -71.732198 },
    { name: 'Támara', lat: 5.829543, lng: -72.16165 },
    { name: 'Tauramena', lat: 5.018977, lng: -72.74662 },
    { name: 'Trinidad', lat: 5.412178, lng: -71.662812 },
    { name: 'Villanueva', lat: 4.61035, lng: -72.927797 },
    { name: 'Yopal', lat: 5.327102, lng: -72.396132 },
  ],
  Cauca: [
    { name: 'Almaguer', lat: 1.913429, lng: -76.85607 },
    { name: 'Argelia', lat: 2.257427, lng: -77.24905 },
    { name: 'Balboa', lat: 2.040998, lng: -77.215773 },
    { name: 'Bolívar', lat: 1.837538, lng: -76.966215 },
    { name: 'Buenos Aires', lat: 3.015382, lng: -76.642238 },
    { name: 'Cajibío', lat: 2.623371, lng: -76.570682 },
    { name: 'Caldono', lat: 2.798059, lng: -76.484319 },
    { name: 'Caloto', lat: 3.034531, lng: -76.408941 },
    { name: 'Corinto', lat: 3.173854, lng: -76.261866 },
    { name: 'El Tambo', lat: 2.451409, lng: -76.810911 },
    { name: 'Florencia', lat: 1.682535, lng: -77.072547 },
    { name: 'Guachené', lat: 3.134153, lng: -76.392189 },
    { name: 'Guapi', lat: 2.571337, lng: -77.88797 },
    { name: 'Inzá', lat: 2.549183, lng: -76.063503 },
    { name: 'Jambaló', lat: 2.777834, lng: -76.323877 },
    { name: 'La Sierra', lat: 2.179383, lng: -76.763278 },
    { name: 'La Vega', lat: 2.001803, lng: -76.778771 },
    { name: 'López de Micay', lat: 2.846788, lng: -77.247803 },
    { name: 'Mercaderes', lat: 1.789193, lng: -77.164319 },
    { name: 'Miranda', lat: 3.254651, lng: -76.228722 },
    { name: 'Morales', lat: 2.754684, lng: -76.629106 },
    { name: 'Padilla', lat: 3.220984, lng: -76.313265 },
    { name: 'Páez', lat: 2.645724, lng: -75.970685 },
    { name: 'Patía', lat: 2.115875, lng: -76.981075 },
    { name: 'Piamonte', lat: 1.11754, lng: -76.327588 },
    { name: 'Piendamó - Tunía', lat: 2.64228, lng: -76.528615 },
    { name: 'Popayán', lat: 2.459641, lng: -76.599377 },
    { name: 'Puerto Tejada', lat: 3.233254, lng: -76.417673 },
    { name: 'Puracé', lat: 2.341507, lng: -76.496698 },
    { name: 'Rosas', lat: 2.260941, lng: -76.740336 },
    { name: 'San Sebastián', lat: 1.838451, lng: -76.769467 },
    { name: 'Santa Rosa', lat: 1.700916, lng: -76.573252 },
    { name: 'Santander de Quilichao', lat: 3.015008, lng: -76.485141 },
    { name: 'Silvia', lat: 2.611927, lng: -76.379753 },
    { name: 'Sotará - Paispamba', lat: 2.253156, lng: -76.613365 },
    { name: 'Suárez', lat: 2.959785, lng: -76.69357 },
    { name: 'Sucre', lat: 2.038237, lng: -76.926279 },
    { name: 'Timbío', lat: 2.349686, lng: -76.684476 },
    { name: 'Timbiquí', lat: 2.777312, lng: -77.667541 },
    { name: 'Toribío', lat: 2.953017, lng: -76.270284 },
    { name: 'Totoró', lat: 2.510252, lng: -76.403628 },
    { name: 'Villa Rica', lat: 3.17762, lng: -76.458025 },
  ],
  Cesar: [
    { name: 'Aguachica', lat: 8.306811, lng: -73.614027 },
    { name: 'Agustín Codazzi', lat: 10.040454, lng: -73.238389 },
    { name: 'Astrea', lat: 9.498062, lng: -73.975842 },
    { name: 'Becerril', lat: 9.704404, lng: -73.278707 },
    { name: 'Bosconia', lat: 9.975098, lng: -73.888761 },
    { name: 'Chimichagua', lat: 9.25875, lng: -73.813278 },
    { name: 'Chiriguaná', lat: 9.361058, lng: -73.599913 },
    { name: 'Curumaní', lat: 9.201716, lng: -73.540843 },
    { name: 'El Copey', lat: 10.149883, lng: -73.962703 },
    { name: 'El Paso', lat: 9.668461, lng: -73.742012 },
    { name: 'Gamarra', lat: 8.324793, lng: -73.737558 },
    { name: 'González', lat: 8.389604, lng: -73.38004 },
    { name: 'La Gloria', lat: 8.619298, lng: -73.80321 },
    { name: 'La Jagua de Ibirico', lat: 9.563752, lng: -73.334143 },
    { name: 'La Paz', lat: 10.387552, lng: -73.171365 },
    { name: 'Manaure Balcón del Cesar', lat: 10.390776, lng: -73.029472 },
    { name: 'Pailitas', lat: 8.959399, lng: -73.625825 },
    { name: 'Pelaya', lat: 8.689451, lng: -73.666735 },
    { name: 'Pueblo Bello', lat: 10.417321, lng: -73.586211 },
    { name: 'Río de Oro', lat: 8.292292, lng: -73.386393 },
    { name: 'San Alberto', lat: 7.76111, lng: -73.393889 },
    { name: 'San Diego', lat: 10.333039, lng: -73.181208 },
    { name: 'San Martín', lat: 7.999855, lng: -73.510914 },
    { name: 'Tamalameque', lat: 8.861725, lng: -73.812172 },
    { name: 'Valledupar', lat: 10.460472, lng: -73.259398 },
  ],
  'Chocó': [
    { name: 'Acandí', lat: 8.512178, lng: -77.279951 },
    { name: 'Alto Baudó', lat: 5.516221, lng: -76.974373 },
    { name: 'Atrato', lat: 5.531419, lng: -76.635674 },
    { name: 'Bagadó', lat: 5.409681, lng: -76.416063 },
    { name: 'Bahía Solano', lat: 6.222807, lng: -77.401359 },
    { name: 'Bajo Baudó', lat: 4.954576, lng: -77.365717 },
    { name: 'Bojayá', lat: 6.559708, lng: -76.886773 },
    { name: 'Carmen del Darién', lat: 7.158294, lng: -76.970798 },
    { name: 'Cértegui', lat: 5.371373, lng: -76.607619 },
    { name: 'Condoto', lat: 5.091003, lng: -76.650683 },
    { name: 'El Cantón del San Pablo', lat: 5.335321, lng: -76.726844 },
    { name: 'El Carmen de Atrato', lat: 5.899789, lng: -76.142112 },
    { name: 'El Litoral del San Juan', lat: 4.259564, lng: -77.363702 },
    { name: 'Istmina', lat: 5.153946, lng: -76.68518 },
    { name: 'Juradó', lat: 7.103619, lng: -77.762751 },
    { name: 'Lloró', lat: 5.49789, lng: -76.545147 },
    { name: 'Medio Atrato', lat: 5.994935, lng: -76.783042 },
    { name: 'Medio Baudó', lat: 5.192471, lng: -76.950891 },
    { name: 'Medio San Juan', lat: 5.098291, lng: -76.694409 },
    { name: 'Nóvita', lat: 4.956063, lng: -76.609467 },
    { name: 'Nuevo Belén de Bajirá', lat: 7.3719, lng: -76.71727 },
    { name: 'Nuquí', lat: 5.709812, lng: -77.265507 },
    { name: 'Quibdó', lat: 5.682166, lng: -76.638144 },
    { name: 'Río Iró', lat: 5.1863, lng: -76.472925 },
    { name: 'Río Quito', lat: 5.483667, lng: -76.740684 },
    { name: 'Riosucio', lat: 7.436704, lng: -77.113156 },
    { name: 'San José del Palmar', lat: 4.896954, lng: -76.234227 },
    { name: 'Sipí', lat: 4.65262, lng: -76.643453 },
    { name: 'Tadó', lat: 5.264873, lng: -76.558571 },
    { name: 'Unguía', lat: 8.04406, lng: -77.092538 },
    { name: 'Unión Panamericana', lat: 5.281108, lng: -76.630143 },
  ],
  'Córdoba': [
    { name: 'Ayapel', lat: 8.313838, lng: -75.146048 },
    { name: 'Buenavista', lat: 8.221187, lng: -75.480897 },
    { name: 'Canalete', lat: 8.786939, lng: -76.241476 },
    { name: 'Cereté', lat: 8.888532, lng: -75.796093 },
    { name: 'Chimá', lat: 9.149698, lng: -75.626886 },
    { name: 'Chinú', lat: 9.105473, lng: -75.399633 },
    { name: 'Ciénaga de Oro', lat: 8.875794, lng: -75.620807 },
    { name: 'Cotorra', lat: 9.037163, lng: -75.799216 },
    { name: 'La Apartada', lat: 8.050125, lng: -75.336031 },
    { name: 'Lorica', lat: 9.240789, lng: -75.816084 },
    { name: 'Los Córdobas', lat: 8.892098, lng: -76.35518 },
    { name: 'Momil', lat: 9.240707, lng: -75.67796 },
    { name: 'Montelíbano', lat: 7.973777, lng: -75.416818 },
    { name: 'Montería', lat: 8.759789, lng: -75.873096 },
    { name: 'Moñitos', lat: 9.245223, lng: -76.1291 },
    { name: 'Planeta Rica', lat: 8.4082, lng: -75.583241 },
    { name: 'Pueblo Nuevo', lat: 8.504099, lng: -75.508035 },
    { name: 'Puerto Escondido', lat: 9.005372, lng: -76.260411 },
    { name: 'Puerto Libertador', lat: 7.888859, lng: -75.671761 },
    { name: 'Purísima de la Concepción', lat: 9.239295, lng: -75.724987 },
    { name: 'Sahagún', lat: 8.943048, lng: -75.445834 },
    { name: 'San Andrés de Sotavento', lat: 9.145448, lng: -75.50879 },
    { name: 'San Antero', lat: 9.376434, lng: -75.76112 },
    { name: 'San Bernardo del Viento', lat: 9.35247, lng: -75.955107 },
    { name: 'San Carlos', lat: 8.799282, lng: -75.698799 },
    { name: 'San José de Uré', lat: 7.787303, lng: -75.533476 },
    { name: 'San Pelayo', lat: 8.958436, lng: -75.835615 },
    { name: 'Tierralta', lat: 8.170612, lng: -76.059797 },
    { name: 'Tuchín', lat: 9.186625, lng: -75.553962 },
    { name: 'Valencia', lat: 8.255016, lng: -76.150756 },
  ],
  Cundinamarca: [
    { name: 'Agua de Dios', lat: 4.375309, lng: -74.669221 },
    { name: 'Albán', lat: 4.878022, lng: -74.438261 },
    { name: 'Anapoima', lat: 4.562737, lng: -74.528676 },
    { name: 'Anolaima', lat: 4.7617, lng: -74.46384 },
    { name: 'Apulo', lat: 4.520304, lng: -74.593926 },
    { name: 'Arbeláez', lat: 4.272534, lng: -74.414901 },
    { name: 'Beltrán', lat: 4.802832, lng: -74.741666 },
    { name: 'Bituima', lat: 4.872171, lng: -74.539609 },
    { name: 'Bojacá', lat: 4.737205, lng: -74.344594 },
    { name: 'Cabrera', lat: 3.985164, lng: -74.484549 },
    { name: 'Cachipay', lat: 4.730957, lng: -74.435711 },
    { name: 'Cajicá', lat: 4.920009, lng: -74.02298 },
    { name: 'Caparrapí', lat: 5.34758, lng: -74.491045 },
    { name: 'Cáqueza', lat: 4.404112, lng: -73.946473 },
    { name: 'Carmen de Carupa', lat: 5.349119, lng: -73.901357 },
    { name: 'Chaguaní', lat: 4.948916, lng: -74.593455 },
    { name: 'Chía', lat: 4.866508, lng: -74.05 },
    { name: 'Chipaque', lat: 4.442671, lng: -74.044876 },
    { name: 'Choachí', lat: 4.527048, lng: -73.922894 },
    { name: 'Chocontá', lat: 5.145224, lng: -73.683533 },
    { name: 'Cogua', lat: 5.061842, lng: -73.978497 },
    { name: 'Cota', lat: 4.812564, lng: -74.102569 },
    { name: 'Cucunubá', lat: 5.249795, lng: -73.766113 },
    { name: 'El Colegio', lat: 4.577951, lng: -74.442261 },
    { name: 'El Peñón', lat: 5.248747, lng: -74.290207 },
    { name: 'El Rosal', lat: 4.850589, lng: -74.263103 },
    { name: 'Facatativá', lat: 4.813353, lng: -74.350085 },
    { name: 'Fómeque', lat: 4.485474, lng: -73.892523 },
    { name: 'Fosca', lat: 4.339093, lng: -73.93902 },
    { name: 'Funza', lat: 4.710412, lng: -74.201528 },
    { name: 'Fúquene', lat: 5.403997, lng: -73.795855 },
    { name: 'Fusagasugá', lat: 4.336723, lng: -74.37543 },
    { name: 'Gachalá', lat: 4.693579, lng: -73.520161 },
    { name: 'Gachancipá', lat: 4.990947, lng: -73.873464 },
    { name: 'Gachetá', lat: 4.816411, lng: -73.636377 },
    { name: 'Gama', lat: 4.763325, lng: -73.611037 },
    { name: 'Girardot', lat: 4.313069, lng: -74.798201 },
    { name: 'Granada', lat: 4.519763, lng: -74.350766 },
    { name: 'Guachetá', lat: 5.383378, lng: -73.686972 },
    { name: 'Guaduas', lat: 5.072076, lng: -74.603402 },
    { name: 'Guasca', lat: 4.866719, lng: -73.877143 },
    { name: 'Guataquí', lat: 4.517517, lng: -74.790058 },
    { name: 'Guatavita', lat: 4.935211, lng: -73.83293 },
    { name: 'Guayabal de Síquima', lat: 4.877968, lng: -74.467437 },
    { name: 'Guayabetal', lat: 4.215306, lng: -73.815107 },
    { name: 'Gutiérrez', lat: 4.254679, lng: -74.003042 },
    { name: 'Jerusalén', lat: 4.562273, lng: -74.695474 },
    { name: 'Junín', lat: 4.79057, lng: -73.662961 },
    { name: 'La Calera', lat: 4.721104, lng: -73.968161 },
    { name: 'La Mesa', lat: 4.631028, lng: -74.461588 },
    { name: 'La Palma', lat: 5.358816, lng: -74.391022 },
    { name: 'La Peña', lat: 5.198945, lng: -74.394105 },
    { name: 'La Vega', lat: 4.997768, lng: -74.336885 },
    { name: 'Lenguazaque', lat: 5.306131, lng: -73.711512 },
    { name: 'Machetá', lat: 5.08007, lng: -73.608226 },
    { name: 'Madrid', lat: 4.732791, lng: -74.265854 },
    { name: 'Manta', lat: 5.009008, lng: -73.540444 },
    { name: 'Medina', lat: 4.506298, lng: -73.348449 },
    { name: 'Mosquera', lat: 4.70653, lng: -74.221154 },
    { name: 'Nariño', lat: 4.399837, lng: -74.824732 },
    { name: 'Nemocón', lat: 5.068705, lng: -73.877888 },
    { name: 'Nilo', lat: 4.305838, lng: -74.620009 },
    { name: 'Nimaima', lat: 5.125992, lng: -74.38604 },
    { name: 'Nocaima', lat: 5.069466, lng: -74.379093 },
    { name: 'Pacho', lat: 5.136907, lng: -74.156132 },
    { name: 'Paime', lat: 5.370487, lng: -74.152213 },
    { name: 'Pandi', lat: 4.190393, lng: -74.486641 },
    { name: 'Paratebueno', lat: 4.374832, lng: -73.212825 },
    { name: 'Pasca', lat: 4.308979, lng: -74.302276 },
    { name: 'Puerto Salgar', lat: 5.465413, lng: -74.653695 },
    { name: 'Pulí', lat: 4.682022, lng: -74.71438 },
    { name: 'Quebradanegra', lat: 5.118076, lng: -74.48014 },
    { name: 'Quetame', lat: 4.329884, lng: -73.863214 },
    { name: 'Quipile', lat: 4.74481, lng: -74.533705 },
    { name: 'Ricaurte', lat: 4.282113, lng: -74.772861 },
    { name: 'San Antonio del Tequendama', lat: 4.616138, lng: -74.351443 },
    { name: 'San Bernardo', lat: 4.179433, lng: -74.42296 },
    { name: 'San Cayetano', lat: 5.332938, lng: -74.024754 },
    { name: 'San Francisco', lat: 4.972917, lng: -74.289672 },
    { name: 'San Juan de Rioseco', lat: 4.847575, lng: -74.621919 },
    { name: 'Sasaima', lat: 4.962167, lng: -74.432628 },
    { name: 'Sesquilé', lat: 5.04476, lng: -73.796099 },
    { name: 'Sibaté', lat: 4.492625, lng: -74.257874 },
    { name: 'Silvania', lat: 4.381981, lng: -74.405534 },
    { name: 'Simijaca', lat: 5.505231, lng: -73.850703 },
    { name: 'Soacha', lat: 4.579268, lng: -74.215463 },
    { name: 'Sopó', lat: 4.915395, lng: -73.943328 },
    { name: 'Subachoque', lat: 4.929118, lng: -74.172773 },
    { name: 'Suesca', lat: 5.103495, lng: -73.798227 },
    { name: 'Supatá', lat: 5.06162, lng: -74.235403 },
    { name: 'Susa', lat: 5.455291, lng: -73.813938 },
    { name: 'Sutatausa', lat: 5.247482, lng: -73.853159 },
    { name: 'Tabio', lat: 4.916832, lng: -74.096461 },
    { name: 'Tausa', lat: 5.196333, lng: -73.887813 },
    { name: 'Tena', lat: 4.655286, lng: -74.389193 },
    { name: 'Tenjo', lat: 4.872014, lng: -74.143724 },
    { name: 'Tibacuy', lat: 4.348605, lng: -74.452662 },
    { name: 'Tibirita', lat: 5.052278, lng: -73.504514 },
    { name: 'Tocaima', lat: 4.459279, lng: -74.636296 },
    { name: 'Tocancipá', lat: 4.964641, lng: -73.91207 },
    { name: 'Topaipí', lat: 5.336224, lng: -74.300626 },
    { name: 'Ubalá', lat: 4.74762, lng: -73.531489 },
    { name: 'Ubaque', lat: 4.483788, lng: -73.933477 },
    { name: 'Une', lat: 4.40245, lng: -74.025183 },
    { name: 'Útica', lat: 5.19055, lng: -74.483154 },
    { name: 'Venecia', lat: 4.089056, lng: -74.478301 },
    { name: 'Vergara', lat: 5.117258, lng: -74.346163 },
    { name: 'Vianí', lat: 4.875208, lng: -74.56132 },
    { name: 'Villa de San Diego de Ubaté', lat: 5.307463, lng: -73.814367 },
    { name: 'Villagómez', lat: 5.273024, lng: -74.195145 },
    { name: 'Villapinzón', lat: 5.216393, lng: -73.595704 },
    { name: 'Villeta', lat: 5.012754, lng: -74.469686 },
    { name: 'Viotá', lat: 4.43935, lng: -74.523131 },
    { name: 'Yacopí', lat: 5.459272, lng: -74.33806 },
    { name: 'Zipacón', lat: 4.759932, lng: -74.379566 },
    { name: 'Zipaquirá', lat: 5.025477, lng: -73.994444 },
  ],
  'Guainía': [
    { name: 'Barrancominas', lat: 3.494178, lng: -69.814066 },
    { name: 'Cacahual', lat: 3.52617, lng: -67.413312 },
    { name: 'Inírida', lat: 3.866764, lng: -67.918613 },
    { name: 'La Guadalupe', lat: 1.632464, lng: -66.963692 },
    { name: 'Morichal', lat: 2.265132, lng: -69.919404 },
    { name: 'Pana Pana', lat: 1.865668, lng: -69.0099 },
    { name: 'Puerto Colombia', lat: 2.726438, lng: -67.566774 },
    { name: 'San Felipe', lat: 1.912495, lng: -67.067848 },
  ],
  Guaviare: [
    { name: 'Calamar', lat: 1.960982, lng: -72.655197 },
    { name: 'El Retorno', lat: 2.330164, lng: -72.627304 },
    { name: 'Miraflores', lat: 1.337539, lng: -71.950416 },
    { name: 'San José del Guaviare', lat: 2.565932, lng: -72.639254 },
  ],
  Huila: [
    { name: 'Acevedo', lat: 1.805173, lng: -75.888706 },
    { name: 'Agrado', lat: 2.25987, lng: -75.772022 },
    { name: 'Aipe', lat: 3.223996, lng: -75.239017 },
    { name: 'Algeciras', lat: 2.521674, lng: -75.315389 },
    { name: 'Altamira', lat: 2.063841, lng: -75.788471 },
    { name: 'Baraya', lat: 3.152204, lng: -75.054843 },
    { name: 'Campoalegre', lat: 2.686767, lng: -75.325748 },
    { name: 'Colombia', lat: 3.376745, lng: -74.802815 },
    { name: 'Elías', lat: 2.012854, lng: -75.938301 },
    { name: 'Garzón', lat: 2.196493, lng: -75.627057 },
    { name: 'Gigante', lat: 2.384031, lng: -75.547681 },
    { name: 'Guadalupe', lat: 2.02426, lng: -75.757185 },
    { name: 'Hobo', lat: 2.580812, lng: -75.447697 },
    { name: 'Íquira', lat: 2.649359, lng: -75.634497 },
    { name: 'Isnos', lat: 1.929467, lng: -76.217637 },
    { name: 'La Argentina', lat: 2.198496, lng: -75.979763 },
    { name: 'La Plata', lat: 2.389263, lng: -75.891254 },
    { name: 'Nátaga', lat: 2.5451, lng: -75.808756 },
    { name: 'Neiva', lat: 2.935432, lng: -75.277327 },
    { name: 'Oporapa', lat: 2.025088, lng: -75.995165 },
    { name: 'Paicol', lat: 2.449651, lng: -75.773158 },
    { name: 'Palermo', lat: 2.889649, lng: -75.435296 },
    { name: 'Palestina', lat: 1.723725, lng: -76.133251 },
    { name: 'Pital', lat: 2.266618, lng: -75.804544 },
    { name: 'Pitalito', lat: 1.852631, lng: -76.049441 },
    { name: 'Rivera', lat: 2.777586, lng: -75.258753 },
    { name: 'Saladoblanco', lat: 1.9934, lng: -76.044747 },
    { name: 'San Agustín', lat: 1.881081, lng: -76.27036 },
    { name: 'Santa María', lat: 2.939603, lng: -75.586223 },
    { name: 'Suaza', lat: 1.976051, lng: -75.79525 },
    { name: 'Tarqui', lat: 2.111325, lng: -75.823976 },
    { name: 'Tello', lat: 3.067538, lng: -75.138773 },
    { name: 'Teruel', lat: 2.740968, lng: -75.567034 },
    { name: 'Tesalia', lat: 2.486364, lng: -75.730271 },
    { name: 'Timaná', lat: 1.974539, lng: -75.932167 },
    { name: 'Villavieja', lat: 3.218822, lng: -75.217174 },
    { name: 'Yaguará', lat: 2.664694, lng: -75.518023 },
  ],
  'La Guajira': [
    { name: 'Albania', lat: 11.151628, lng: -72.61232 },
    { name: 'Barrancas', lat: 10.958669, lng: -72.793639 },
    { name: 'Dibulla', lat: 11.27155, lng: -73.307598 },
    { name: 'Distracción', lat: 10.898414, lng: -72.887405 },
    { name: 'El Molino', lat: 10.653505, lng: -72.92673 },
    { name: 'Fonseca', lat: 10.886734, lng: -72.846319 },
    { name: 'Hatonuevo', lat: 11.068864, lng: -72.75904 },
    { name: 'La Jagua del Pilar', lat: 10.511862, lng: -73.072638 },
    { name: 'Maicao', lat: 11.378535, lng: -72.242738 },
    { name: 'Manaure', lat: 11.773767, lng: -72.438739 },
    { name: 'Riohacha', lat: 11.528588, lng: -72.911795 },
    { name: 'San Juan del Cesar', lat: 10.769546, lng: -73.000629 },
    { name: 'Uribia', lat: 11.711904, lng: -72.265906 },
    { name: 'Urumita', lat: 10.560169, lng: -73.012507 },
    { name: 'Villanueva', lat: 10.608774, lng: -72.977583 },
  ],
  Magdalena: [
    { name: 'Algarrobo', lat: 10.188059, lng: -74.061132 },
    { name: 'Aracataca', lat: 10.589791, lng: -74.186702 },
    { name: 'Ariguaní', lat: 9.847047, lng: -74.236515 },
    { name: 'Cerro de San Antonio', lat: 10.325531, lng: -74.868474 },
    { name: 'Chivolo', lat: 10.026631, lng: -74.622242 },
    { name: 'Ciénaga', lat: 11.006654, lng: -74.241286 },
    { name: 'Concordia', lat: 10.257314, lng: -74.83303 },
    { name: 'El Banco', lat: 9.008503, lng: -73.97437 },
    { name: 'El Piñón', lat: 10.402781, lng: -74.823094 },
    { name: 'El Retén', lat: 10.610488, lng: -74.268444 },
    { name: 'Fundación', lat: 10.514146, lng: -74.191453 },
    { name: 'Guamal', lat: 9.144354, lng: -74.223689 },
    { name: 'Nueva Granada', lat: 9.80186, lng: -74.391841 },
    { name: 'Pedraza', lat: 10.18825, lng: -74.9154 },
    { name: 'Pijiño del Carmen', lat: 9.331922, lng: -74.459034 },
    { name: 'Pivijay', lat: 10.460707, lng: -74.613312 },
    { name: 'Plato', lat: 9.796713, lng: -74.784549 },
    { name: 'Puebloviejo', lat: 10.994766, lng: -74.28253 },
    { name: 'Remolino', lat: 10.701952, lng: -74.716172 },
    { name: 'Sabanas de San Ángel', lat: 10.032536, lng: -74.213946 },
    { name: 'Salamina', lat: 10.491229, lng: -74.794189 },
    { name: 'San Sebastián de Buenavista', lat: 9.241656, lng: -74.351498 },
    { name: 'San Zenón', lat: 9.245061, lng: -74.498992 },
    { name: 'Santa Ana', lat: 9.324294, lng: -74.566845 },
    { name: 'Santa Bárbara de Pinto', lat: 9.432263, lng: -74.704667 },
    { name: 'Santa Marta', lat: 11.204679, lng: -74.199829 },
    { name: 'Sitionuevo', lat: 10.775285, lng: -74.720021 },
    { name: 'Tenerife', lat: 9.898273, lng: -74.859783 },
    { name: 'Zapayán', lat: 10.168297, lng: -74.716878 },
    { name: 'Zona Bananera', lat: 10.763024, lng: -74.140091 },
  ],
  Meta: [
    { name: 'Acacías', lat: 3.990413, lng: -73.766034 },
    { name: 'Barranca de Upía', lat: 4.566225, lng: -72.961083 },
    { name: 'Cabuyaro', lat: 4.286705, lng: -72.791768 },
    { name: 'Castilla la Nueva', lat: 3.830005, lng: -73.687302 },
    { name: 'Cubarral', lat: 3.793653, lng: -73.837999 },
    { name: 'Cumaral', lat: 4.270042, lng: -73.487052 },
    { name: 'El Calvario', lat: 4.352665, lng: -73.713325 },
    { name: 'El Castillo', lat: 3.563907, lng: -73.794225 },
    { name: 'El Dorado', lat: 3.739984, lng: -73.835264 },
    { name: 'Fuente de Oro', lat: 3.462875, lng: -73.618121 },
    { name: 'Granada', lat: 3.547147, lng: -73.705815 },
    { name: 'Guamal', lat: 3.879657, lng: -73.768815 },
    { name: 'La Macarena', lat: 2.177143, lng: -73.78661 },
    { name: 'Lejanías', lat: 3.525115, lng: -74.023514 },
    { name: 'Mapiripán', lat: 2.896617, lng: -72.135509 },
    { name: 'Mesetas', lat: 3.382732, lng: -74.044328 },
    { name: 'Puerto Concordia', lat: 2.624006, lng: -72.760209 },
    { name: 'Puerto Gaitán', lat: 4.314905, lng: -72.087649 },
    { name: 'Puerto Lleras', lat: 3.272117, lng: -73.37385 },
    { name: 'Puerto López', lat: 4.09349, lng: -72.957324 },
    { name: 'Puerto Rico', lat: 2.939621, lng: -73.206314 },
    { name: 'Restrepo', lat: 4.259556, lng: -73.565408 },
    { name: 'San Carlos de Guaroa', lat: 3.71065, lng: -73.242253 },
    { name: 'San Juan de Arama', lat: 3.373728, lng: -73.875832 },
    { name: 'San Juanito', lat: 4.458181, lng: -73.676699 },
    { name: 'San Martín', lat: 3.701899, lng: -73.695812 },
    { name: 'Uribe', lat: 3.239634, lng: -74.351508 },
    { name: 'Villavicencio', lat: 4.126369, lng: -73.622601 },
    { name: 'Vistahermosa', lat: 3.125579, lng: -73.750966 },
  ],
  'Nariño': [
    { name: 'Albán', lat: 1.474978, lng: -77.080712 },
    { name: 'Aldana', lat: 0.882381, lng: -77.700564 },
    { name: 'Ancuya', lat: 1.263276, lng: -77.514512 },
    { name: 'Arboleda', lat: 1.503418, lng: -77.135467 },
    { name: 'Barbacoas', lat: 1.671733, lng: -78.13765 },
    { name: 'Belén', lat: 1.595681, lng: -77.015619 },
    { name: 'Buesaco', lat: 1.381453, lng: -77.156463 },
    { name: 'Chachagüí', lat: 1.360545, lng: -77.281869 },
    { name: 'Colón', lat: 1.643878, lng: -77.019777 },
    { name: 'Consacá', lat: 1.207854, lng: -77.466136 },
    { name: 'Contadero', lat: 0.910458, lng: -77.549409 },
    { name: 'Córdoba', lat: 0.854564, lng: -77.517897 },
    { name: 'Cuaspud Carlosama', lat: 0.862978, lng: -77.728947 },
    { name: 'Cumbal', lat: 0.906367, lng: -77.792505 },
    { name: 'Cumbitara', lat: 1.647163, lng: -77.578616 },
    { name: 'El Charco', lat: 2.479688, lng: -78.110217 },
    { name: 'El Peñol', lat: 1.453567, lng: -77.438522 },
    { name: 'El Rosario', lat: 1.745309, lng: -77.33417 },
    { name: 'El Tablón de Gómez', lat: 1.427277, lng: -77.097101 },
    { name: 'El Tambo', lat: 1.407913, lng: -77.390772 },
    { name: 'Francisco Pizarro', lat: 2.040629, lng: -78.658361 },
    { name: 'Funes', lat: 1.001159, lng: -77.448913 },
    { name: 'Guachucal', lat: 0.959744, lng: -77.731589 },
    { name: 'Guaitarilla', lat: 1.129574, lng: -77.549824 },
    { name: 'Gualmatán', lat: 0.919652, lng: -77.568701 },
    { name: 'Iles', lat: 0.96952, lng: -77.521227 },
    { name: 'Imués', lat: 1.05506, lng: -77.496339 },
    { name: 'Ipiales', lat: 0.827732, lng: -77.646367 },
    { name: 'La Cruz', lat: 1.601318, lng: -76.970504 },
    { name: 'La Florida', lat: 1.29753, lng: -77.402882 },
    { name: 'La Llanada', lat: 1.472892, lng: -77.58091 },
    { name: 'La Tola', lat: 2.398999, lng: -78.189725 },
    { name: 'La Unión', lat: 1.600219, lng: -77.131316 },
    { name: 'Leiva', lat: 1.934453, lng: -77.306135 },
    { name: 'Linares', lat: 1.350814, lng: -77.523953 },
    { name: 'Los Andes', lat: 1.494587, lng: -77.521303 },
    { name: 'Magüí', lat: 1.765633, lng: -78.182924 },
    { name: 'Mallama', lat: 1.141037, lng: -77.864549 },
    { name: 'Mosquera', lat: 2.507139, lng: -78.452992 },
    { name: 'Nariño', lat: 1.288979, lng: -77.357972 },
    { name: 'Olaya Herrera', lat: 2.347457, lng: -78.325814 },
    { name: 'Ospina', lat: 1.058433, lng: -77.566082 },
    { name: 'Pasto', lat: 1.212352, lng: -77.278795 },
    { name: 'Policarpa', lat: 1.627196, lng: -77.458686 },
    { name: 'Potosí', lat: 0.806639, lng: -77.573003 },
    { name: 'Providencia', lat: 1.237814, lng: -77.596794 },
    { name: 'Puerres', lat: 0.885125, lng: -77.504211 },
    { name: 'Pupiales', lat: 0.870442, lng: -77.636042 },
    { name: 'Ricaurte', lat: 1.212492, lng: -77.995153 },
    { name: 'Roberto Payán', lat: 1.697492, lng: -78.245716 },
    { name: 'Samaniego', lat: 1.335438, lng: -77.594341 },
    { name: 'San Andrés de Tumaco', lat: 1.807399, lng: -78.764073 },
    { name: 'San Bernardo', lat: 1.513762, lng: -77.0475 },
    { name: 'San Lorenzo', lat: 1.503362, lng: -77.21542 },
    { name: 'San Pablo', lat: 1.669429, lng: -77.013984 },
    { name: 'San Pedro de Cartago', lat: 1.551572, lng: -77.11941 },
    { name: 'Sandoná', lat: 1.283438, lng: -77.47313 },
    { name: 'Santa Bárbara', lat: 2.449653, lng: -77.979916 },
    { name: 'Santacruz', lat: 1.222589, lng: -77.677035 },
    { name: 'Sapuyes', lat: 1.037536, lng: -77.62028 },
    { name: 'Taminango', lat: 1.570358, lng: -77.2808 },
    { name: 'Tangua', lat: 1.09482, lng: -77.393735 },
    { name: 'Túquerres', lat: 1.085044, lng: -77.61672 },
    { name: 'Yacuanquer', lat: 1.115937, lng: -77.400169 },
  ],
  'Norte de Santander': [
    { name: 'Ábrego', lat: 8.081616, lng: -73.221722 },
    { name: 'Arboledas', lat: 7.642985, lng: -72.798952 },
    { name: 'Bochalema', lat: 7.612192, lng: -72.64701 },
    { name: 'Bucarasica', lat: 8.041299, lng: -72.868231 },
    { name: 'Cáchira', lat: 7.741248, lng: -73.048983 },
    { name: 'Cácota', lat: 7.268705, lng: -72.642059 },
    { name: 'Chinácota', lat: 7.603112, lng: -72.601162 },
    { name: 'Chitagá', lat: 7.138187, lng: -72.665468 },
    { name: 'Convención', lat: 8.470374, lng: -73.3372 },
    { name: 'Cucutilla', lat: 7.539633, lng: -72.772816 },
    { name: 'Durania', lat: 7.714804, lng: -72.658491 },
    { name: 'El Carmen', lat: 8.510579, lng: -73.446687 },
    { name: 'El Tarra', lat: 8.574281, lng: -73.09614 },
    { name: 'El Zulia', lat: 7.938572, lng: -72.604717 },
    { name: 'Gramalote', lat: 7.916946, lng: -72.787233 },
    { name: 'Hacarí', lat: 8.321506, lng: -73.145997 },
    { name: 'Herrán', lat: 7.506541, lng: -72.483519 },
    { name: 'La Esperanza', lat: 7.639839, lng: -73.328126 },
    { name: 'La Playa', lat: 8.21124, lng: -73.239986 },
    { name: 'Labateca', lat: 7.298414, lng: -72.495983 },
    { name: 'Los Patios', lat: 7.833186, lng: -72.505612 },
    { name: 'Lourdes', lat: 7.945631, lng: -72.832376 },
    { name: 'Mutiscua', lat: 7.300469, lng: -72.747169 },
    { name: 'Ocaña', lat: 8.248574, lng: -73.35607 },
    { name: 'Pamplona', lat: 7.372802, lng: -72.647714 },
    { name: 'Pamplonita', lat: 7.436745, lng: -72.639111 },
    { name: 'Puerto Santander', lat: 8.359993, lng: -72.411363 },
    { name: 'Ragonvalia', lat: 7.577861, lng: -72.476708 },
    { name: 'Salazar', lat: 7.773683, lng: -72.813064 },
    { name: 'San Calixto', lat: 8.40214, lng: -73.208622 },
    { name: 'San Cayetano', lat: 7.875695, lng: -72.625459 },
    { name: 'San José de Cúcuta', lat: 7.905725, lng: -72.508178 },
    { name: 'Santiago', lat: 7.865856, lng: -72.716203 },
    { name: 'Sardinata', lat: 8.082105, lng: -72.800577 },
    { name: 'Silos', lat: 7.204736, lng: -72.757128 },
    { name: 'Teorama', lat: 8.438134, lng: -73.28707 },
    { name: 'Tibú', lat: 8.639891, lng: -72.734496 },
    { name: 'Toledo', lat: 7.307692, lng: -72.481915 },
    { name: 'Villa Caro', lat: 7.914244, lng: -72.973601 },
    { name: 'Villa del Rosario', lat: 7.847672, lng: -72.469758 },
  ],
  Putumayo: [
    { name: 'Colón', lat: 1.190133, lng: -76.972566 },
    { name: 'Mocoa', lat: 1.151172, lng: -76.654238 },
    { name: 'Orito', lat: 0.663593, lng: -76.873276 },
    { name: 'Puerto Asís', lat: 0.505627, lng: -76.496887 },
    { name: 'Puerto Caicedo', lat: 0.687784, lng: -76.606118 },
    { name: 'Puerto Guzmán', lat: 0.962854, lng: -76.407663 },
    { name: 'Puerto Leguízamo', lat: -0.192318, lng: -74.781842 },
    { name: 'San Francisco', lat: 1.174194, lng: -76.879283 },
    { name: 'San Miguel', lat: 0.34346, lng: -76.91217 },
    { name: 'Santiago', lat: 1.147076, lng: -77.002641 },
    { name: 'Sibundoy', lat: 1.20026, lng: -76.917814 },
    { name: 'Valle del Guamuez', lat: 0.423506, lng: -76.906751 },
    { name: 'Villagarzón', lat: 1.028821, lng: -76.61721 },
  ],
  'Quindío': [
    { name: 'Armenia', lat: 4.53598, lng: -75.680786 },
    { name: 'Buenavista', lat: 4.360029, lng: -75.739572 },
    { name: 'Calarcá', lat: 4.520982, lng: -75.646085 },
    { name: 'Circasia', lat: 4.617759, lng: -75.636533 },
    { name: 'Córdoba', lat: 4.392485, lng: -75.687866 },
    { name: 'Filandia', lat: 4.674338, lng: -75.658387 },
    { name: 'Génova', lat: 4.206641, lng: -75.790402 },
    { name: 'La Tebaida', lat: 4.453755, lng: -75.786887 },
    { name: 'Montenegro', lat: 4.565057, lng: -75.749827 },
    { name: 'Pijao', lat: 4.335036, lng: -75.703329 },
    { name: 'Quimbaya', lat: 4.624387, lng: -75.765074 },
    { name: 'Salento', lat: 4.637157, lng: -75.570844 },
  ],
  Risaralda: [
    { name: 'Apía', lat: 5.106526, lng: -75.942356 },
    { name: 'Balboa', lat: 4.949096, lng: -75.958663 },
    { name: 'Belén de Umbría', lat: 5.200793, lng: -75.868334 },
    { name: 'Dosquebradas', lat: 4.833131, lng: -75.675371 },
    { name: 'Guática', lat: 5.315367, lng: -75.799005 },
    { name: 'La Celia', lat: 5.002787, lng: -76.0032 },
    { name: 'La Virginia', lat: 4.896624, lng: -75.880394 },
    { name: 'Marsella', lat: 4.935771, lng: -75.73879 },
    { name: 'Mistrató', lat: 5.297039, lng: -75.882886 },
    { name: 'Pereira', lat: 4.804985, lng: -75.719711 },
    { name: 'Pueblo Rico', lat: 5.222043, lng: -76.030801 },
    { name: 'Quinchía', lat: 5.340456, lng: -75.730431 },
    { name: 'Santa Rosa de Cabal', lat: 4.876271, lng: -75.623268 },
    { name: 'Santuario', lat: 5.074911, lng: -75.964628 },
  ],
  'San Andrés y Providencia': [
    { name: 'Providencia', lat: 13.373185, lng: -81.368386 },
    { name: 'San Andrés', lat: 12.578108, lng: -81.707181 },
  ],
  Santander: [
    { name: 'Aguada', lat: 6.162355, lng: -73.523132 },
    { name: 'Albania', lat: 5.759166, lng: -73.91336 },
    { name: 'Aratoca', lat: 6.694418, lng: -73.01786 },
    { name: 'Barbosa', lat: 5.932531, lng: -73.615965 },
    { name: 'Barichara', lat: 6.634111, lng: -73.223047 },
    { name: 'Barrancabermeja', lat: 7.064857, lng: -73.849243 },
    { name: 'Betulia', lat: 6.899525, lng: -73.283669 },
    { name: 'Bolívar', lat: 5.988953, lng: -73.771346 },
    { name: 'Bucaramanga', lat: 7.11647, lng: -73.132562 },
    { name: 'Cabrera', lat: 6.592118, lng: -73.246475 },
    { name: 'California', lat: 7.347989, lng: -72.946491 },
    { name: 'Capitanejo', lat: 6.527394, lng: -72.695427 },
    { name: 'Carcasí', lat: 6.629016, lng: -72.627099 },
    { name: 'Cepitá', lat: 6.753518, lng: -72.973536 },
    { name: 'Cerrito', lat: 6.840405, lng: -72.694851 },
    { name: 'Charalá', lat: 6.284339, lng: -73.146873 },
    { name: 'Charta', lat: 7.28082, lng: -72.968798 },
    { name: 'Chima', lat: 6.344348, lng: -73.373656 },
    { name: 'Chipatá', lat: 6.062521, lng: -73.637111 },
    { name: 'Cimitarra', lat: 6.320886, lng: -73.953011 },
    { name: 'Concepción', lat: 6.768908, lng: -72.694567 },
    { name: 'Confines', lat: 6.357327, lng: -73.240554 },
    { name: 'Contratación', lat: 6.290561, lng: -73.474426 },
    { name: 'Coromoro', lat: 6.294999, lng: -73.040816 },
    { name: 'Curití', lat: 6.605099, lng: -73.069383 },
    { name: 'El Carmen de Chucurí', lat: 6.700038, lng: -73.51066 },
    { name: 'El Guacamayo', lat: 6.245111, lng: -73.496908 },
    { name: 'El Peñón', lat: 6.05537, lng: -73.815532 },
    { name: 'El Playón', lat: 7.470715, lng: -73.20287 },
    { name: 'Encino', lat: 6.137429, lng: -73.098749 },
    { name: 'Enciso', lat: 6.668034, lng: -72.699647 },
    { name: 'Florián', lat: 5.804659, lng: -73.97143 },
    { name: 'Floridablanca', lat: 7.072329, lng: -73.099104 },
    { name: 'Galán', lat: 6.638423, lng: -73.287769 },
    { name: 'Gámbita', lat: 5.945998, lng: -73.344185 },
    { name: 'Girón', lat: 7.070432, lng: -73.166832 },
    { name: 'Guaca', lat: 6.876563, lng: -72.856322 },
    { name: 'Guadalupe', lat: 6.245847, lng: -73.419292 },
    { name: 'Guapotá', lat: 6.308635, lng: -73.320732 },
    { name: 'Guavatá', lat: 5.954348, lng: -73.700906 },
    { name: 'Güepsa', lat: 6.025013, lng: -73.575146 },
    { name: 'Hato', lat: 6.543957, lng: -73.308399 },
    { name: 'Jesús María', lat: 5.876497, lng: -73.783396 },
    { name: 'Jordán', lat: 6.732727, lng: -73.096053 },
    { name: 'La Belleza', lat: 5.85925, lng: -73.965494 },
    { name: 'La Paz', lat: 6.178509, lng: -73.58959 },
    { name: 'Landázuri', lat: 6.218812, lng: -73.811359 },
    { name: 'Lebrija', lat: 7.113351, lng: -73.219524 },
    { name: 'Los Santos', lat: 6.755203, lng: -73.102739 },
    { name: 'Macaravita', lat: 6.50658, lng: -72.593105 },
    { name: 'Málaga', lat: 6.703081, lng: -72.732089 },
    { name: 'Matanza', lat: 7.323175, lng: -73.015566 },
    { name: 'Mogotes', lat: 6.475246, lng: -72.969807 },
    { name: 'Molagavita', lat: 6.67432, lng: -72.809175 },
    { name: 'Ocamonte', lat: 6.339988, lng: -73.122563 },
    { name: 'Oiba', lat: 6.26521, lng: -73.299791 },
    { name: 'Onzaga', lat: 6.344104, lng: -72.816766 },
    { name: 'Palmar', lat: 6.537789, lng: -73.29109 },
    { name: 'Palmas del Socorro', lat: 6.406139, lng: -73.287764 },
    { name: 'Páramo', lat: 6.416811, lng: -73.17022 },
    { name: 'Piedecuesta', lat: 6.997245, lng: -73.054795 },
    { name: 'Pinchote', lat: 6.531552, lng: -73.174209 },
    { name: 'Puente Nacional', lat: 5.878381, lng: -73.677567 },
    { name: 'Puerto Parra', lat: 6.650785, lng: -74.056129 },
    { name: 'Puerto Wilches', lat: 7.344057, lng: -73.899909 },
    { name: 'Rionegro', lat: 7.265014, lng: -73.150177 },
    { name: 'Sabana de Torres', lat: 7.391919, lng: -73.49906 },
    { name: 'San Andrés', lat: 6.811511, lng: -72.848864 },
    { name: 'San Benito', lat: 6.126656, lng: -73.50907 },
    { name: 'San Gil', lat: 6.551952, lng: -73.134776 },
    { name: 'San Joaquín', lat: 6.427548, lng: -72.867638 },
    { name: 'San José de Miranda', lat: 6.658995, lng: -72.733616 },
    { name: 'San Miguel', lat: 6.575315, lng: -72.644123 },
    { name: 'San Vicente de Chucurí', lat: 6.880383, lng: -73.411024 },
    { name: 'Santa Bárbara', lat: 6.990996, lng: -72.907445 },
    { name: 'Santa Helena del Opón', lat: 6.339565, lng: -73.616716 },
    { name: 'Simacota', lat: 6.443469, lng: -73.337368 },
    { name: 'Socorro', lat: 6.46387, lng: -73.261198 },
    { name: 'Suaita', lat: 6.101329, lng: -73.44165 },
    { name: 'Sucre', lat: 5.918743, lng: -73.790975 },
    { name: 'Suratá', lat: 7.36658, lng: -72.984232 },
    { name: 'Tona', lat: 7.201417, lng: -72.967023 },
    { name: 'Valle de San José', lat: 6.448028, lng: -73.143507 },
    { name: 'Vélez', lat: 6.009275, lng: -73.672447 },
    { name: 'Vetas', lat: 7.30981, lng: -72.871041 },
    { name: 'Villanueva', lat: 6.670078, lng: -73.174307 },
    { name: 'Zapatoca', lat: 6.814387, lng: -73.268034 },
  ],
  Sucre: [
    { name: 'Buenavista', lat: 9.319794, lng: -74.972827 },
    { name: 'Caimito', lat: 8.789324, lng: -75.117141 },
    { name: 'Chalán', lat: 9.545352, lng: -75.312697 },
    { name: 'Colosó', lat: 9.494192, lng: -75.353256 },
    { name: 'Corozal', lat: 9.318749, lng: -75.293048 },
    { name: 'Coveñas', lat: 9.402779, lng: -75.680158 },
    { name: 'El Roble', lat: 9.100647, lng: -75.198378 },
    { name: 'Galeras', lat: 9.160379, lng: -75.04959 },
    { name: 'Guaranda', lat: 8.468556, lng: -74.537749 },
    { name: 'La Unión', lat: 8.853975, lng: -75.276056 },
    { name: 'Los Palmitos', lat: 9.380269, lng: -75.268716 },
    { name: 'Majagual', lat: 8.541163, lng: -74.628077 },
    { name: 'Morroa', lat: 9.331395, lng: -75.305949 },
    { name: 'Ovejas', lat: 9.527176, lng: -75.229037 },
    { name: 'Palmito', lat: 9.333157, lng: -75.541264 },
    { name: 'Sampués', lat: 9.183193, lng: -75.380222 },
    { name: 'San Benito Abad', lat: 8.930108, lng: -75.031089 },
    { name: 'San José de Toluviejo', lat: 9.451819, lng: -75.44085 },
    { name: 'San Juan de Betulia', lat: 9.273066, lng: -75.243565 },
    { name: 'San Luis de Sincé', lat: 9.244308, lng: -75.145999 },
    { name: 'San Marcos', lat: 8.661774, lng: -75.133831 },
    { name: 'San Onofre', lat: 9.736955, lng: -75.522398 },
    { name: 'San Pedro', lat: 9.39625, lng: -75.063647 },
    { name: 'Santiago de Tolú', lat: 9.525387, lng: -75.581112 },
    { name: 'Sincelejo', lat: 9.302322, lng: -75.395445 },
    { name: 'Sucre', lat: 8.811737, lng: -74.723175 },
  ],
  Tolima: [
    { name: 'Alpujarra', lat: 3.391548, lng: -74.9329 },
    { name: 'Alvarado', lat: 4.567356, lng: -74.953418 },
    { name: 'Ambalema', lat: 4.782682, lng: -74.764429 },
    { name: 'Anzoátegui', lat: 4.631756, lng: -75.093772 },
    { name: 'Armero', lat: 5.030744, lng: -74.884438 },
    { name: 'Ataco', lat: 3.590591, lng: -75.382545 },
    { name: 'Cajamarca', lat: 4.438812, lng: -75.431971 },
    { name: 'Carmen de Apicalá', lat: 4.152334, lng: -74.717633 },
    { name: 'Casabianca', lat: 5.078465, lng: -75.120966 },
    { name: 'Chaparral', lat: 3.722918, lng: -75.480765 },
    { name: 'Coello', lat: 4.287276, lng: -74.898464 },
    { name: 'Coyaima', lat: 3.798036, lng: -75.193862 },
    { name: 'Cunday', lat: 4.059259, lng: -74.692227 },
    { name: 'Dolores', lat: 3.539072, lng: -74.896761 },
    { name: 'Espinal', lat: 4.151314, lng: -74.885446 },
    { name: 'Falan', lat: 5.123104, lng: -74.953007 },
    { name: 'Flandes', lat: 4.276373, lng: -74.818763 },
    { name: 'Fresno', lat: 5.153576, lng: -75.035722 },
    { name: 'Guamo', lat: 4.030992, lng: -74.968135 },
    { name: 'Herveo', lat: 5.080228, lng: -75.177151 },
    { name: 'Honda', lat: 5.211816, lng: -74.75699 },
    { name: 'Ibagué', lat: 4.432248, lng: -75.19425 },
    { name: 'Icononzo', lat: 4.176487, lng: -74.531969 },
    { name: 'Lérida', lat: 4.862046, lng: -74.910716 },
    { name: 'Líbano', lat: 4.92042, lng: -75.061959 },
    { name: 'Melgar', lat: 4.203655, lng: -74.641317 },
    { name: 'Murillo', lat: 4.874341, lng: -75.171022 },
    { name: 'Natagaima', lat: 3.624324, lng: -75.093182 },
    { name: 'Ortega', lat: 3.934916, lng: -75.222601 },
    { name: 'Palocabildo', lat: 5.120918, lng: -75.022167 },
    { name: 'Piedras', lat: 4.543951, lng: -74.878106 },
    { name: 'Planadas', lat: 3.197911, lng: -75.644163 },
    { name: 'Prado', lat: 3.750939, lng: -74.927447 },
    { name: 'Purificación', lat: 3.857246, lng: -74.935555 },
    { name: 'Rioblanco', lat: 3.529932, lng: -75.644069 },
    { name: 'Roncesvalles', lat: 4.011567, lng: -75.605959 },
    { name: 'Rovira', lat: 4.239019, lng: -75.240648 },
    { name: 'Saldaña', lat: 3.929815, lng: -75.016852 },
    { name: 'San Antonio', lat: 3.914146, lng: -75.480074 },
    { name: 'San Luis', lat: 4.133721, lng: -75.095804 },
    { name: 'San Sebastián de Mariquita', lat: 5.199708, lng: -74.889276 },
    { name: 'Santa Isabel', lat: 4.713606, lng: -75.097934 },
    { name: 'Suárez', lat: 4.048891, lng: -74.831885 },
    { name: 'Valle de San Juan', lat: 4.197494, lng: -75.115669 },
    { name: 'Venadillo', lat: 4.717878, lng: -74.929333 },
    { name: 'Villahermosa', lat: 5.030452, lng: -75.117729 },
    { name: 'Villarrica', lat: 3.936902, lng: -74.600285 },
  ],
  'Valle del Cauca': [
    { name: 'Alcalá', lat: 4.674994, lng: -75.779792 },
    { name: 'Andalucía', lat: 4.171713, lng: -76.167925 },
    { name: 'Ansermanuevo', lat: 4.794984, lng: -75.992003 },
    { name: 'Argelia', lat: 4.726945, lng: -76.119905 },
    { name: 'Bolívar', lat: 4.337846, lng: -76.183583 },
    { name: 'Buenaventura', lat: 3.875708, lng: -77.01074 },
    { name: 'Bugalagrande', lat: 4.208358, lng: -76.15682 },
    { name: 'Caicedonia', lat: 4.334808, lng: -75.830594 },
    { name: 'Calima', lat: 3.933664, lng: -76.484132 },
    { name: 'Candelaria', lat: 3.408354, lng: -76.346519 },
    { name: 'Cartago', lat: 4.742192, lng: -75.924374 },
    { name: 'Dagua', lat: 3.657318, lng: -76.68886 },
    { name: 'El Águila', lat: 4.906062, lng: -76.042779 },
    { name: 'El Cairo', lat: 4.760874, lng: -76.221611 },
    { name: 'El Cerrito', lat: 3.684229, lng: -76.311972 },
    { name: 'El Dovio', lat: 4.510452, lng: -76.237084 },
    { name: 'Florida', lat: 3.324118, lng: -76.234199 },
    { name: 'Ginebra', lat: 3.724181, lng: -76.268068 },
    { name: 'Guacarí', lat: 3.761815, lng: -76.330911 },
    { name: 'Guadalajara de Buga', lat: 3.900736, lng: -76.298979 },
    { name: 'Jamundí', lat: 3.258751, lng: -76.538472 },
    { name: 'La Cumbre', lat: 3.649268, lng: -76.56805 },
    { name: 'La Unión', lat: 4.533869, lng: -76.099661 },
    { name: 'La Victoria', lat: 4.523603, lng: -76.036529 },
    { name: 'Obando', lat: 4.575712, lng: -75.974709 },
    { name: 'Palmira', lat: 3.531544, lng: -76.298846 },
    { name: 'Pradera', lat: 3.419793, lng: -76.241799 },
    { name: 'Restrepo', lat: 3.821351, lng: -76.523329 },
    { name: 'Riofrío', lat: 4.156908, lng: -76.288313 },
    { name: 'Roldanillo', lat: 4.413601, lng: -76.152277 },
    { name: 'San Pedro', lat: 3.995073, lng: -76.228692 },
    { name: 'Santiago de Cali', lat: 3.413686, lng: -76.52133 },
    { name: 'Sevilla', lat: 4.270714, lng: -75.931629 },
    { name: 'Toro', lat: 4.608085, lng: -76.076859 },
    { name: 'Trujillo', lat: 4.212037, lng: -76.318818 },
    { name: 'Tuluá', lat: 4.085399, lng: -76.197731 },
    { name: 'Ulloa', lat: 4.703623, lng: -75.737808 },
    { name: 'Versalles', lat: 4.575019, lng: -76.199203 },
    { name: 'Vijes', lat: 3.698686, lng: -76.441804 },
    { name: 'Yotoco', lat: 3.861241, lng: -76.382698 },
    { name: 'Yumbo', lat: 3.540097, lng: -76.499893 },
    { name: 'Zarzal', lat: 4.392658, lng: -76.070795 },
  ],
  'Vaupés': [
    { name: 'Carurú', lat: 1.016116, lng: -71.30221 },
    { name: 'Mitú', lat: 1.253151, lng: -70.232641 },
    { name: 'Pacoa', lat: 0.020698, lng: -71.004339 },
    { name: 'Papunahua', lat: 1.908124, lng: -70.76091 },
    { name: 'Taraira', lat: -0.564984, lng: -69.635497 },
    { name: 'Yavaraté', lat: 0.609142, lng: -69.203337 },
  ],
  Vichada: [
    { name: 'Cumaribo', lat: 4.446352, lng: -69.795533 },
    { name: 'La Primavera', lat: 5.486309, lng: -70.410515 },
    { name: 'Puerto Carreño', lat: 6.186636, lng: -67.487095 },
    { name: 'Santa Rosalía', lat: 5.136393, lng: -70.859499 },
  ],
  'Bogotá D.C.': [
    { name: 'Bogotá, D.c.', lat: 4.649251, lng: -74.106992 },
  ],
};

/**
 * Centro aproximado de cada departamento, calculado como el **promedio de los centroides de
 * sus municipios** (DANE `gdxc-w37w`). Es una **aproximación para centrar el mapa**, no un dato
 * geográfico oficial: sirve para encuadrar la zona cuando el operador sólo eligió el
 * departamento y todavía no marcó un punto ni un municipio.
 */
export const COLOMBIA_DEPARTMENT_CENTERS: Readonly<Record<string, { lat: number; lng: number }>> = {
  Amazonas: { lat: -1.817071, lng: -71.454173 },
  Antioquia: { lat: 6.543971, lng: -75.56049 },
  Arauca: { lat: 6.698438, lng: -71.268261 },
  'Atlántico': { lat: 10.698521, lng: -74.903653 },
  'Bolívar': { lat: 9.34892, lng: -74.649477 },
  'Boyacá': { lat: 5.666555, lng: -73.208477 },
  Caldas: { lat: 5.283082, lng: -75.437532 },
  'Caquetá': { lat: 1.39772, lng: -75.492781 },
  Casanare: { lat: 5.378863, lng: -72.304513 },
  Cauca: { lat: 2.497873, lng: -76.674244 },
  Cesar: { lat: 9.304605, lng: -73.54132 },
  'Chocó': { lat: 5.800946, lng: -76.824489 },
  'Córdoba': { lat: 8.760054, lng: -75.732234 },
  Cundinamarca: { lat: 4.818657, lng: -74.181681 },
  'Guainía': { lat: 2.661164, lng: -68.209201 },
  Guaviare: { lat: 2.048654, lng: -72.468043 },
  Huila: { lat: 2.431761, lng: -75.659129 },
  'La Guajira': { lat: 11.048834, lng: -72.803706 },
  Magdalena: { lat: 10.125802, lng: -74.482 },
  Meta: { lat: 3.672573, lng: -73.487007 },
  'Nariño': { lat: 1.400515, lng: -77.570785 },
  'Norte de Santander': { lat: 7.852623, lng: -72.822067 },
  Putumayo: { lat: 0.791243, lng: -76.617574 },
  'Quindío': { lat: 4.493634, lng: -75.701299 },
  Risaralda: { lat: 5.061129, lng: -75.844131 },
  'San Andrés y Providencia': { lat: 12.975647, lng: -81.537783 },
  Santander: { lat: 6.554152, lng: -73.261618 },
  Sucre: { lat: 9.195532, lng: -75.208664 },
  Tolima: { lat: 4.323421, lng: -75.041262 },
  'Valle del Cauca': { lat: 4.11143, lng: -76.237145 },
  'Vaupés': { lat: 0.707041, lng: -70.356489 },
  Vichada: { lat: 5.313923, lng: -69.63816 },
  'Bogotá D.C.': { lat: 4.649251, lng: -74.106992 },
};
